import { useT } from '@transifex/react'
import { AddressTypes } from '@vend/business-components/dist/components/EditCustomerModal/DetailsTab/Address'
import { FulfilmentDetail } from '../../../../../../models/fulfilment'
import { useAppSelector } from '../../../../../../state/hooks'
import { selectSaleCustomer } from '../../../../../../state/slices/currentSale/currentSaleSlice'
import {
  AddressErrorsStatus,
  AddressErrorStatusTypes,
} from '../../popover/hooks/useFulfillmentHelper'
import { useLocalizedAddress } from '../../popover/hooks/useLocalizedAddress'
import { getCustomerAddressDetails } from '../../popover/utils/addressHelper'
import { EditAddressButtons } from '../EditAddressButton/EditAddressButton.type'
import { useAddressModalHandlers } from '../hooks/useAddressModalHandlers'

export interface AddressesProps {
  isSameBillingAddress: boolean
  formattedShippingAddress: string
  formattedBillingAddress?: string
  isModalOpeningOnError?: boolean
  hasError: boolean
  addressStatus: AddressErrorStatusTypes
  fulfilmentDetail?: FulfilmentDetail
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
  lineItemLocalId?: string
}

export const useAddressesModel = ({
  addressStatus,
  formattedBillingAddress,
  formattedShippingAddress,
  fulfilmentDetail,
  isModalOpeningOnError,
  isSameBillingAddress,
  onSave,
}: AddressesProps) => {
  const t = useT()

  const shippingAddressTitle = isSameBillingAddress
    ? t('Shipping & billing address', { _context: 'Section title' })
    : t('Shipping address', { _context: 'Section title' })

  const billingAddressTitle = t('Billing address', {
    _context: 'Section title',
  })

  const formatLocalizedAddress = useLocalizedAddress()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const formattedSaleCustomerShippingAddress = saleCustomer
    ? formatLocalizedAddress(
        getCustomerAddressDetails(saleCustomer, AddressTypes.Shipping)
      )
    : undefined
  const formattedSaleCustomerBillingAddress = saleCustomer
    ? formatLocalizedAddress(
        getCustomerAddressDetails(saleCustomer, AddressTypes.Billing)
      )
    : undefined
  const isAddressChanged =
    formattedSaleCustomerShippingAddress !== formattedShippingAddress ||
    formattedSaleCustomerBillingAddress !== formattedBillingAddress

  const hasBillingAddressAndNoShippingAddressInModal =
    isModalOpeningOnError &&
    (addressStatus === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
      addressStatus === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING)

  const noShippingAddressModalTitle = t(
    'No shipping address added for this customer',
    {
      _context: 'Modal title',
    }
  )

  const noShippingAddressModalDescription =
    addressStatus === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING
      ? t('You can use the billing address for shipping or add a new one.', {
          _context: 'Modal description',
        })
      : t(
          'Fix billing address errors to use it for shipping. You can also add a new shipping and billing address for this sale.',
          {
            _context: 'Modal description',
          }
        )

  const changedAddressMessage = t('Address changes apply to this sale only', {
    _context:
      'Note explaining that address updates apply only to a specific sale',
  })

  const { openAddressModal } = useAddressModalHandlers({
    type: EditAddressButtons.edit,
    fulfilmentDetail,
    onSave,
  })

  return {
    billingAddressTitle,
    changedAddressMessage,
    hasBillingAddressAndNoShippingAddressInModal,
    noShippingAddressModalDescription,
    noShippingAddressModalTitle,
    openAddressModal,
    shippingAddressTitle,
    shouldShowAddressChangedMessage: isAddressChanged && !!saleCustomer,
    shouldShowBillingAddress: !isSameBillingAddress,
  }
}
